/**
 * Audit Log Component
 * Comprehensive audit logging dashboard for system administration
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  Pagination,
  Stack,
  Avatar,
  Tooltip,
  Badge,
  LinearProgress,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  TrendingUp as TrendingUpIcon,
  Shield as ShieldIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';
import MetricCard from '../common/MetricCard';
import { colors, spacing } from '../../theme/designTokens';

interface AuditLogEntry {
  id: string;
  timestamp: string;
  user_id: number;
  user_email: string;
  user_name?: string;
  user_role: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  ip_address: string;
  user_agent: string;
  details?: any;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  status: 'success' | 'failure' | 'warning';
  location?: string;
  session_id?: string;
}

interface AuditMetrics {
  totalEvents: number;
  criticalEvents: number;
  failedLogins: number;
  successfulLogins: number;
  adminActions: number;
  dataModifications: number;
  securityEvents: number;
  uniqueUsers: number;
  riskDistribution: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  recentTrends: {
    hourly: number[];
    daily: number[];
  };
}

const AuditLog: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [metrics, setMetrics] = useState<AuditMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    action: '',
    user: '',
    riskLevel: '',
    status: '',
    // No time filter here: the audit endpoint takes page/limit only, and the
    // Select that used to write `dateRange` was never sent and never applied.
    search: '',
  });

  const loadAuditLog = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getAuditLog(page, 50);

      console.log('[Audit Log] API Response:', response);

      // Backend returns { success: true, audit_log: [...], pagination: {...} }
      if (response && response.audit_log && Array.isArray(response.audit_log)) {
        setAuditEntries(response.audit_log);

        const total = response.pagination?.total || response.audit_log.length;
        const pageSize = response.pagination?.limit || 50;
        setTotalPages(Math.ceil(total / pageSize));
      } else {
        console.warn('[Audit Log] No audit entries in response:', response);
        setAuditEntries([]);
        setTotalPages(1);
      }
    } catch (error: any) {
      console.error('[Audit Log] Error loading audit log:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.message || 'Failed to load audit log',
      }));
      setAuditEntries([]);
    } finally {
      setLoading(false);
    }
  }, [page, dispatch]);

  const loadMetrics = useCallback(async () => {
    try {
      // Mock metrics for now - replace with actual API call when available
      const mockMetrics: AuditMetrics = {
        totalEvents: 15247,
        criticalEvents: 23,
        failedLogins: 156,
        successfulLogins: 2847,
        adminActions: 492,
        dataModifications: 1038,
        securityEvents: 67,
        uniqueUsers: 312,
        riskDistribution: {
          low: 12456,
          medium: 2234,
          high: 534,
          critical: 23,
        },
        recentTrends: {
          hourly: Array.from({length: 24}, () => Math.floor(Math.random() * 50) + 20),
          daily: Array.from({length: 7}, () => Math.floor(Math.random() * 500) + 200),
        },
      };
      setMetrics(mockMetrics);
    } catch (error) {
      console.error('Failed to load audit metrics:', error);
    }
  }, []);

  useEffect(() => {
    loadAuditLog();
    loadMetrics();
  }, [loadAuditLog, loadMetrics]);

  const handleRefresh = () => {
    loadAuditLog();
    loadMetrics();
    dispatch(addNotification({
      type: 'success',
      message: 'Audit log refreshed',
    }));
  };

  const handleExport = () => {
    dispatch(addNotification({
      type: 'info',
      message: 'Exporting audit log...',
    }));
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical':
        return theme.palette.error.main;
      case 'high':
        return theme.palette.warning.main;
      case 'medium':
        return theme.palette.info.main;
      case 'low':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'failure':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionIcon = (action: string) => {
    if (action.includes('login')) return <PersonIcon />;
    if (action.includes('admin')) return <AdminIcon />;
    if (action.includes('security')) return <ShieldIcon />;
    return <ViewIcon />;
  };

  return (
    <Box sx={{ p: spacing[6], bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: spacing[8] }}>
        <Box>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: spacing[3],
              mb: spacing[2]
            }}
          >
            <SecurityIcon sx={{ fontSize: 40, color: colors.costs.main }} />
            Audit Log
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
            Comprehensive system activity monitoring and security audit trail
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Metrics Overview */}
      {metrics && (
        <Grid container spacing={3} sx={{ mb: spacing[6] }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Events"
              value={metrics.totalEvents}
              subtitle="System-wide activity"
              icon="trending_up"
              color="costs"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Critical Events"
              value={metrics.criticalEvents}
              subtitle="Require attention"
              icon="error"
              color="costs"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Failed Logins"
              value={metrics.failedLogins}
              subtitle="Security alerts"
              icon="warning"
              color="costs"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Active Users"
              value={metrics.uniqueUsers}
              subtitle="Unique administrators"
              icon="person"
              color="costs"
            />
          </Grid>
        </Grid>
      )}

      {/* Risk Distribution */}
      {metrics && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Risk Level Distribution
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(metrics.riskDistribution).map(([level, count]) => (
                <Grid item xs={6} sm={3} key={level}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                      {level}
                    </Typography>
                    <Typography variant="h6" sx={{ color: getRiskColor(level), fontWeight: 600 }}>
                      {count.toLocaleString()}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(count / metrics.totalEvents) * 100}
                      sx={{ 
                        mt: 1, 
                        height: 6, 
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getRiskColor(level)
                        }
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search logs..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={filters.action}
                  label="Action"
                  onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="login">Login</MenuItem>
                  <MenuItem value="logout">Logout</MenuItem>
                  <MenuItem value="admin">Admin Actions</MenuItem>
                  <MenuItem value="data">Data Changes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Risk Level</InputLabel>
                <Select
                  value={filters.riskLevel}
                  label="Risk Level"
                  onChange={(e) => setFilters(prev => ({ ...prev, riskLevel: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="failure">Failure</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : auditEntries.length > 0 ? (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Resource</TableCell>
                      <TableCell>Risk Level</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>IP Address</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditEntries.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTimestamp(entry.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                              {entry.user_name 
                                ? entry.user_name.split(' ').map(n => n[0]).join('') 
                                : entry.user_email.charAt(0).toUpperCase()
                              }
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {entry.user_name || entry.user_email}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {entry.user_role}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getActionIcon(entry.action)}
                            <Typography variant="body2">
                              {entry.action}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.resource_type}
                            {entry.resource_id && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                ID: {entry.resource_id}
                              </Typography>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.risk_level}
                            size="small"
                            sx={{
                              bgcolor: getRiskColor(entry.risk_level),
                              color: 'white',
                              fontWeight: 500,
                              textTransform: 'capitalize',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusIcon(entry.status)}
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {entry.status}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {entry.ip_address}
                          </Typography>
                          {entry.location && (
                            <Typography variant="caption" color="textSecondary">
                              {entry.location}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            </>
          ) : (
            <Alert severity="info">
              No audit log entries found. This could indicate no recent activity or a configuration issue.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuditLog;